import { Target, Eye, Diamond, Users, Award, ShieldCheck, Lightbulb, Heart } from "lucide-react";
import { AnimatedSection } from "./AnimatedSection";

const AboutUs = () => {
  const values = [
    { icon: ShieldCheck, label: "Integridad", description: "Actuamos con honestidad y transparencia en cada proyecto" },
    { icon: Award, label: "Excelencia", description: "Buscamos la perfección en cada detalle de nuestro trabajo" },
    { icon: Users, label: "Seguridad", description: "Priorizamos la seguridad de personas e instalaciones" },
    { icon: Heart, label: "Compromiso", description: "Cumplimos lo que prometemos, siempre" },
    { icon: Lightbulb, label: "Innovación", description: "Adoptamos las mejores tecnologías del mercado" },
  ];

  return (
    <section id="quienes-somos" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-dark-section via-industrial-gray/95 to-dark-section" />
      
      {/* Animated blobs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <AnimatedSection animation="fade-in" className="text-center mb-16">
          <span className="inline-block px-4 py-2 glass-liquid-medium rounded-full text-primary font-inter font-semibold text-sm mb-4">
            CONÓCENOS
          </span>
          <h2 className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            QUIÉNES SOMOS
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Conoce la historia, misión y valores que nos definen como empresa líder en servicios eléctricos
          </p>
        </AnimatedSection>

        {/* Historia Section */}
        <AnimatedSection animation="slide-up" delay={100} className="mb-16">
          <div className="glass-liquid-dark-heavy rounded-3xl p-8 md:p-12 border-2 border-white/15 shadow-liquid-lg hover:border-white/25 transition-all duration-300">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Image placeholder */}
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden glass-liquid-medium">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-accent/20 to-primary/10 flex items-center justify-center">
                  <Users className="w-24 h-24 text-white/40" />
                </div>
                <div className="absolute bottom-4 left-4 right-4 glass-liquid-dark-medium rounded-xl p-4">
                  <p className="text-white font-montserrat font-bold text-lg">15+ Años</p>
                  <p className="text-white/70 text-sm">de experiencia en el sector</p>
                </div>
              </div>

              {/* Content */}
              <div>
                <h3 className="font-montserrat font-bold text-2xl md:text-3xl text-white mb-6">
                  NUESTRA HISTORIA
                </h3>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-6 text-justify hyphens-auto">
                  Fundada en 2010, <strong className="text-primary">MYCING Ltda.</strong> nació de la visión de crear una empresa de servicios eléctricos que combinara la experiencia técnica con un compromiso inquebrantable hacia la calidad y seguridad.
                </p>
                <p className="text-white/70 text-base leading-relaxed mb-8 text-justify hyphens-auto">
                  Lo que comenzó como un pequeño equipo de electricistas certificados, hoy se ha convertido en un referente en el sector eléctrico chileno, con más de 200 proyectos exitosos y la confianza de grandes empresas como Komatsu, Decathlon, DUOC UC y Claro Arena.
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center glass-liquid-light rounded-xl p-4">
                    <p className="font-montserrat font-bold text-2xl text-primary">200+</p>
                    <p className="text-white/60 text-xs">Proyectos</p>
                  </div>
                  <div className="text-center glass-liquid-light rounded-xl p-4">
                    <p className="font-montserrat font-bold text-2xl text-primary">15+</p>
                    <p className="text-white/60 text-xs">Años</p>
                  </div>
                  <div className="text-center glass-liquid-light rounded-xl p-4">
                    <p className="font-montserrat font-bold text-2xl text-primary">SEC</p>
                    <p className="text-white/60 text-xs">Certificados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Mission & Vision Cards */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-16 items-stretch">
          {/* Misión */}
          <AnimatedSection animation="scale-in" delay={200} className="h-full">
            <div className="glass-liquid-dark-heavy rounded-3xl p-8 md:p-10 border-2 border-white/15 shadow-liquid-lg h-full flex flex-col hover:border-white/25 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center mb-6 border border-blue-400/30 flex-shrink-0">
                <Target className="w-8 h-8 text-blue-400" />
              </div>
              <h3 className="font-montserrat font-bold text-xl md:text-2xl text-white mb-4">
                NUESTRA MISIÓN
              </h3>
              <p className="text-white/70 text-base leading-relaxed flex-grow text-justify hyphens-auto">
                Brindar soluciones eléctricas integrales, confiables y seguras que superen las expectativas de nuestros clientes, contribuyendo al desarrollo y modernización de la infraestructura eléctrica en Chile.
              </p>
            </div>
          </AnimatedSection>

          {/* Visión */}
          <AnimatedSection animation="scale-in" delay={250} className="h-full">
            <div className="glass-liquid-dark-heavy rounded-3xl p-8 md:p-10 border-2 border-white/15 shadow-liquid-lg h-full flex flex-col hover:border-white/25 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center mb-6 border border-green-400/30 flex-shrink-0">
                <Eye className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="font-montserrat font-bold text-xl md:text-2xl text-white mb-4">
                NUESTRA VISIÓN
              </h3>
              <p className="text-white/70 text-base leading-relaxed flex-grow text-justify hyphens-auto">
                Ser reconocidos como la empresa líder en servicios eléctricos industriales y comerciales en Chile, destacando por nuestra excelencia técnica, innovación constante y compromiso con la seguridad.
              </p>
            </div>
          </AnimatedSection>
        </div>

        {/* Values Section */}
        <AnimatedSection animation="fade-in" delay={400}>
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-3 mb-4">
              <Diamond className="w-6 h-6 text-primary" />
              <h3 className="font-montserrat font-bold text-xl md:text-2xl text-white">
                NUESTROS VALORES
              </h3>
            </div>
            <p className="text-white/60 text-sm md:text-base max-w-xl mx-auto">
              Los principios que guían cada acción y decisión en MYCING
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={value.label} animation="scale-in" delay={500 + index * 100}>
                <div className="glass-liquid-dark-medium rounded-2xl p-6 border border-white/10 text-center h-full hover:border-primary/30 transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/30 to-accent/20 flex items-center justify-center mx-auto mb-4 border border-primary/30 group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h4 className="font-montserrat font-bold text-sm md:text-base text-white mb-2">
                    {value.label}
                  </h4>
                  <p className="text-white/50 text-xs leading-relaxed hidden md:block text-justify hyphens-auto">
                    {value.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default AboutUs;
